import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Organization, organizationToCustomer } from '@/shared/types/userTypes';
import { useOrganizationContextSwitch } from './use-account-context-switch';
import { Customer } from '../types/customerTypes';
import { CustomerDetailsTab } from '@/shared/config/customer-details-tabs';
import { ROUTES } from '@/config/routeConfig';
import { getAccountDetailsPath } from '@/shared/utils/account-details-routes';
import { getCustomerDetailsPath } from '@/shared/utils/customer-details-routes';
import { AccountDetailsTab } from '@/shared/config/account-details-tabs';
import { useUser } from '@/contexts/UserContext';

export interface AccountDetailsNavigationParams {
  organization: Organization;
  accountDetailsTab?: AccountDetailsTab;
  replace?: boolean;
}

/**
 * Account context switching and navigation helpers.
 * Used by TopBar (tenant users) and admin list pages (account pick).
 */
export function useRouteNavigation() {
  const navigate = useNavigate();
  const { switchOrganizationContext, isSwitchingOrganizationContext } = useOrganizationContextSwitch();
  const { selectedOrganization, selectedParentOrganization } = useUser();

  const switchOrganization = useCallback(async (organization: Organization, parentOrganization?: Organization | null) => {
    if (selectedOrganization?.organizationId === organization.organizationId &&
      (parentOrganization !== null ? selectedParentOrganization?.organizationId === parentOrganization?.organizationId : true))
      return true;
    return await switchOrganizationContext(organization, parentOrganization ?? undefined);
  }, [selectedOrganization, selectedParentOrganization, switchOrganizationContext]);

  const navigateToAccountDetails = useCallback(
    ({ organization, accountDetailsTab, replace }: AccountDetailsNavigationParams) => {
      navigate(getAccountDetailsPath(organization.organizationId, accountDetailsTab), {
        replace,
      });
    },
    [navigate],
  );

  const switchOrganizationAndNavigateToAccountDetails = useCallback(
    async ({ organization, accountDetailsTab, replace }: AccountDetailsNavigationParams) => {
      if (!organization || isSwitchingOrganizationContext) return;
      const switched = await switchOrganization(organization);
      if (!switched) return;
      navigateToAccountDetails({ organization, accountDetailsTab, replace: replace ?? false });
    },
    [switchOrganization, isSwitchingOrganizationContext, navigateToAccountDetails],
  );

  const navigateToAccountServiceLineDetails = useCallback(
    (serviceLineNumber: string, replace?: boolean) => {
      navigate(`${ROUTES.SERVICE_LINE_DETAILS.replace(':serviceLineNumber', serviceLineNumber)}`, {
        state: { serviceLineNumber },
        replace,
      });
    },
    [navigate],
  );

  const switchOrganizationAndNavigateToServiceLineDetails = useCallback(
    async (organization: Organization, serviceLineNumber: string, replace?: boolean | false) => {
      if (!serviceLineNumber || isSwitchingOrganizationContext) return;
      const switched = await switchOrganization(organization);
      if (!switched) return;
      navigateToAccountServiceLineDetails(serviceLineNumber, replace);
    },
    [switchOrganization, isSwitchingOrganizationContext, navigateToAccountServiceLineDetails],
  );

  const switchOrganizationAndNavigateToCustomerDetails = useCallback(
    async (organization: Organization, parentOrganization: Organization, replace?: boolean | false) => {
      if (isSwitchingOrganizationContext) return;

      const switched = await switchOrganization(organization, parentOrganization);
      if (!switched) return;
      const customer = organizationToCustomer(organization);
      navigateToCustomerDetails(customer, CustomerDetailsTab.DASHBOARD, replace);
    },
    [selectedOrganization, selectedParentOrganization, switchOrganizationContext, isSwitchingOrganizationContext],
  );

  const navigateToCustomerDetails = useCallback(
    (customer: Customer, customerDetailsTab?: CustomerDetailsTab, replace?: boolean) => {
      navigate(getCustomerDetailsPath(customer.name, customerDetailsTab), {
        state: { customer },
        replace,
      });
    },
    [navigate],
  );

  return {
    navigateToAccountDetails,
    navigateToCustomerDetails,
    switchOrganizationAndNavigateToAccountDetails,
    switchOrganizationAndNavigateToServiceLineDetails,
    switchOrganizationAndNavigateToCustomerDetails,
    isSwitchingOrganizationContext
  };
}
